import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchReportTickets } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { prettyLabel } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

export function TicketsReportPage() {
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-tickets', qBuilt],
    queryFn: () => fetchReportTickets(qBuilt),
  });

  const d = q.data;

  const statusPairs = useMemo(() => {
    const b = d?.byStatus ?? {};
    return Object.entries(b)
      .map(([status, total]) => ({ status: prettyLabel(status), total }))
      .sort((a, b) => b.total - a.total);
  }, [d]);

  const csv = () => {
    if (!d) return;
    exportToCsv(
      (d.chart ?? []).map((r) => ({
        date: r.date,
        opened: r.opened,
        closed: r.closed,
        cancelled: r.cancelled,
      })),
      'tickets-flow.csv',
    );
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading tickets…</div>;
  if (q.isError || !d) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load ticket report.</div>;

  const s = d.summary;

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {[
          { label: 'Opened', v: s.totalOpened },
          { label: 'Closed', v: s.totalClosed },
          { label: 'Cancelled', v: s.totalCancelled },
          { label: 'Avg resolve (hrs)', v: s.avgResolutionTimeHours.toFixed(1) },
          { label: 'Overdue', v: s.overdueCount },
          { label: 'Warranty', v: s.warrantyCount },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-slate-500">{k.label}</p>
            <p className="mt-1 text-xl font-bold text-slate-950">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Opened vs closed</h3>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="opened" stroke="#6366f1" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="closed" stroke="#22c55e" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="cancelled" stroke="#f97316" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">By status</h3>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusPairs}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="status" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={72} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h4 className="text-xs font-bold uppercase text-slate-500">Priority</h4>
          <ul className="mt-2 space-y-1 text-sm">
            <li>Normal: {d.byPriority.normal}</li>
            <li>Rush: {d.byPriority.rush}</li>
            <li>Urgent: {d.byPriority.urgent}</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h4 className="text-xs font-bold uppercase text-slate-500">Source</h4>
          <ul className="mt-2 space-y-1 text-sm">
            <li>Walk-in: {d.bySource.walk_in}</li>
            <li>Phone: {d.bySource.phone}</li>
            <li>Online: {d.bySource.online}</li>
            <li>Warranty: {d.bySource.warranty}</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h4 className="text-xs font-bold uppercase text-slate-500">Avg age by status (hrs)</h4>
          <ul className="mt-2 max-h-40 space-y-1 overflow-auto text-sm">
            {d.avgTimeByStatus.map((x) => (
              <li key={x.status}>
                {prettyLabel(x.status)}: {x.avgHours.toFixed(1)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
